// @flow

import React, { useEffect, useState } from 'react';
import { observer, inject } from 'mobx-react';
import { useLocation, useNavigate } from 'react-router-dom';
import qs from 'qs';

import paramsToQueryString from 'helpers/paramsToQueryString';
import UpdateQueryParamValues from 'helpers/UpdateQueryParamValues';
import RemoveQueryParamValues from 'helpers/RemoveQueryParamValues';
import updateLangSwitcher from 'helpers/updateLangSwitcher';
import flattenObject from 'helpers/flattenObject';
import { jobs as t } from 'helpers/translation';

import { filters, locale, companyId } from 'core/config';
import usePrev from 'core/hooks/usePrev';

import Pagination from 'components/Pagination';

import Header from './components/Header';
import Filters from './components/Filters';
import Jobs from './components/Jobs';

import store from './store';

type Props = {
  sessionStore: {
    getUser: Function,
    user: any,
    isCurrentUser: boolean,
    handleLike: Function
  }
};

const IndustryCategoryScreen = inject('sessionStore')(observer((props: Props) => {
  const { jobs, loading, totalRecords, perPage, counts, showFilters, toggleVisibility, getJobs } = store;
  const { sessionStore: { user, isCurrentUser, handleLike, getUser } } = props;
  const currentLang = filters.find(filter => filter.url_param === 'job_language_ids').data.find(lang => lang.iso6391 === locale)

  const location = useLocation();
  const prevLocation = usePrev(location);
  const navigate = useNavigate();

  const getSearch = () => {
    const {
      sort, page, q, category_ids, industry_ids, work_type_ids,
      entry_level_ids, job_language_ids, number_employees_group_ids, region_ids
    } = flattenObject(qs.parse(location.search, { ignoreQueryPrefix: true }));
    return {
      sort, page, q, category_ids, industry_ids, work_type_ids,
      entry_level_ids, job_language_ids, number_employees_group_ids, region_ids
    };
  };

  const [search, setSearch] = useState(getSearch());

  const urlParams = (searchParams: any) => {
    const {
      sort, page, q, category_ids, industry_ids, work_type_ids,
      entry_level_ids, job_language_ids, number_employees_group_ids, region_ids
    } = searchParams;

    const customParamsObject = {
      page: {
        offset: Math.ceil((page - 1 || 0) * perPage),
        limit: perPage
      },
      filter: {
        query: q,
        company_id: companyId,
        category_ids: (category_ids && category_ids.join(',')),
        industry_ids: (industry_ids && industry_ids.join(',')),
        work_type_ids: work_type_ids && work_type_ids.join(','),
        entry_level_ids: entry_level_ids && entry_level_ids.join(','),
        job_language_ids: job_language_ids && job_language_ids.join(','),
        number_employees_group_ids: number_employees_group_ids && number_employees_group_ids.join(','),
        region_ids: region_ids && region_ids.join(',')
      },
      locale
    };

    return { sort, ...customParamsObject };
  };

  const pushToSearch = (searchParams: {}, action: 'update' | 'remove' = 'update'): void => {
    const queryString = paramsToQueryString(
      action === 'update'
        ? UpdateQueryParamValues(searchParams, search)
        : RemoveQueryParamValues(searchParams, search)
    );

    navigate(`?${queryString}`);
  };

  useEffect(() => {
    const currentJobParams = urlParams(search);

    if (!search.job_language_ids) {
      pushToSearch({ page: 0, job_language_ids: currentLang.id.toString() }, 'update');
    } else {
      getJobs(currentJobParams);
    }

    getUser();
  }, []);

  useEffect(() => {
    const updatedSearch = getSearch();
    setSearch(updatedSearch);

    updateLangSwitcher(location.search);

    if (prevLocation && updatedSearch !== prevLocation.search) getJobs(urlParams(updatedSearch));
  }, [location]);

  const currentPage = (searchParams: any) => +searchParams.page - 1 || 0;

  const updateSearch = (searchParams: {}): void => {
    navigate(`?${paramsToQueryString(searchParams)}`);
  };

  const handlePageClick = (page: any = {}) => {
    if (page >= 0) pushToSearch({ page });
  };

  const renderMain = () => (
    jobs.length > 0 ? <>
      <Jobs
        jobs={jobs}
        user={user}
        isCurrentUser={isCurrentUser}
        handleLike={handleLike}
      />

      <Pagination
        pageCount={totalRecords}
        perPage={perPage}
        forcePage={currentPage(search)}
        handlePageClick={handlePageClick}
      />
    </> : <div className='card no-results'>
      <h2 className="no-results__info">{t.no_jobs_found}</h2>
      <button type="button" className="btn btn_primary" onClick={() => updateSearch({})}>
        {t.reset_filters}
      </button>
    </div>
  );

  return (
    <>
      <Header
        search={search}
        urlSearch={location.search}
        updateSearch={updateSearch}
        showFilters={showFilters}
        toggleVisibility={toggleVisibility}
        pushToSearch={pushToSearch}
      />

      <Filters
        showFilters={showFilters}
        counts={counts}
        search={search}
        urlSearch={location.search}
        pushToSearch={pushToSearch}
        updateSearch={updateSearch}
        toggleVisibility={toggleVisibility}
      />
      {loading ? 'Loading...' : renderMain()}
    </>
  );
}));

export default IndustryCategoryScreen;
